/**
 * @fileoverview Voice input hook using Web Speech API
 *
 * Provides voice-to-text functionality for dashboard commands.
 * Uses the native Web Speech API with fallback support for webkit.
 */

import { useState, useRef, useCallback, useEffect } from 'react';

// Debug flag - set to true to enable console logging
const DEBUG = true;
const log = (...args) => DEBUG && console.log(...args);

const RECOGNITION_TIMEOUT = 10000; // 10 seconds
const SILENCE_TIMEOUT = 3000; // 3 seconds of silence (single mode)
const CONTINUOUS_SILENCE_TIMEOUT = 5000; // 5 seconds of silence (continuous mode)

export function useVoiceInput(options = {}) {
  const {
    continuousSilenceTimeout = CONTINUOUS_SILENCE_TIMEOUT,
    singleSilenceTimeout = SILENCE_TIMEOUT
  } = options;
  const [isSupported, setIsSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [continuousMode, setContinuousMode] = useState(false);
  const [isActivelySpeaking, setIsActivelySpeaking] = useState(false);
  const [accumulatedFinalResults, setAccumulatedFinalResults] = useState([]);
  const [isWaitingForMoreSpeech, setIsWaitingForMoreSpeech] = useState(false);
  const [wakeWord, setWakeWord] = useState(null); // The current wake word
  const [isWaitingForWakeWord, setIsWaitingForWakeWord] = useState(false); // Whether we're waiting for wake word
  const [wakeWordActiveUntil, setWakeWordActiveUntil] = useState(null); // Timestamp when wake word session expires

  // Keep refs in sync with state
  useEffect(() => {
    continuousModeRef.current = continuousMode;
  }, [continuousMode]);

  useEffect(() => {
    wakeWordRef.current = wakeWord;
  }, [wakeWord]);

  useEffect(() => {
    isWaitingForWakeWordRef.current = isWaitingForWakeWord;
  }, [isWaitingForWakeWord]);

  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const restartTimeoutRef = useRef(null);
  const isManualStopRef = useRef(false);
  const continuousModeRef = useRef(false);
  const finalResultTimeoutRef = useRef(null);
  const wakeWordRef = useRef(null);
  const isWaitingForWakeWordRef = useRef(false);
  const wakeWordTimeoutRef = useRef(null);

  // Check for Web Speech API support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (SpeechRecognition) {
      setIsSupported(true);

      // Initialize recognition
      const recognition = new SpeechRecognition();
      recognition.continuous = true;  // Keep listening continuously
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      // Handle recognition results
      recognition.onresult = (event) => {
        let interim = '';
        let final = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const transcriptText = result[0].transcript;

          if (result.isFinal) {
            final += transcriptText;
            setConfidence(result[0].confidence || 0);
          } else {
            interim += transcriptText;
          }
        }

        if (final) {
          // Clear actively speaking state when we get a final result
          setIsActivelySpeaking(false);

          // In continuous mode, accumulate final results and delay processing
          if (continuousModeRef.current) {
            log('📝 Got final result in continuous mode, waiting for more speech...', final.trim());

            // Add this final result to our accumulated list
            setAccumulatedFinalResults(prev => {
              const newResults = [...prev, final.trim()];
              log('📝 Accumulated results so far:', newResults);
              return newResults;
            });

            // Set waiting state to show loading indicator
            setIsWaitingForMoreSpeech(true);

            // Update display to show all accumulated results
            setAccumulatedFinalResults(prev => {
              const allText = prev.join(' ');
              setInterimTranscript(allText);
              return prev;
            });

            // Clear any existing final result timeout
            if (finalResultTimeoutRef.current) {
              clearTimeout(finalResultTimeoutRef.current);
            }

            // Wait for the configured timeout before processing ALL accumulated results
            finalResultTimeoutRef.current = setTimeout(() => {
              setAccumulatedFinalResults(prev => {
                const completeText = prev.join(' ').toLowerCase().trim();
                log('⏰ Processing ALL accumulated results:', completeText);

                // Check for wake word commands first
                const setWakeWordMatch = completeText.match(/^set wake word (.+)$/);
                if (setWakeWordMatch) {
                  const newWakeWord = setWakeWordMatch[1].trim();
                  log('🔔 Setting wake word to:', newWakeWord);
                  setWakeWord(newWakeWord);
                  setIsWaitingForWakeWord(true);
                  setWakeWordActiveUntil(null);
                  // Clear everything and continue listening for wake word
                  setTranscript('');
                  setInterimTranscript('');
                  setIsProcessing(false);
                  setIsWaitingForMoreSpeech(false);
                  setAccumulatedFinalResults([]);
                  return [];
                }

                // Check for disable wake word command
                if (completeText === 'disable wake word') {
                  log('🔕 Disabling wake word');
                  setWakeWord(null);
                  setIsWaitingForWakeWord(false);
                  setWakeWordActiveUntil(null);
                  if (wakeWordTimeoutRef.current) {
                    clearTimeout(wakeWordTimeoutRef.current);
                    wakeWordTimeoutRef.current = null;
                  }
                  // Clear everything and continue normal operation
                  setTranscript('');
                  setInterimTranscript('');
                  setIsProcessing(false);
                  setIsWaitingForMoreSpeech(false);
                  setAccumulatedFinalResults([]);
                  return [];
                }

                // If we have a wake word enabled, check wake word logic
                if (wakeWordRef.current) {
                  const now = Date.now();

                  // If we're waiting for the wake word
                  if (isWaitingForWakeWordRef.current) {
                    if (completeText.includes(wakeWordRef.current)) {
                      log('🔔 Wake word detected:', wakeWordRef.current);
                      // Wake word detected - enter active session for 10 seconds
                      setIsWaitingForWakeWord(false);
                      setWakeWordActiveUntil(now + 10000); // 10 seconds from now

                      // Remove wake word from the text and process the rest
                      const textWithoutWakeWord = completeText.replace(wakeWordRef.current, '').trim();
                      if (textWithoutWakeWord) {
                        log('🎯 Processing command after wake word:', textWithoutWakeWord);
                        setTranscript(textWithoutWakeWord);
                        setInterimTranscript('');
                        setIsProcessing(true);
                      } else {
                        // Just wake word, clear everything and wait for command
                        setTranscript('');
                        setInterimTranscript('');
                        setIsProcessing(false);
                      }
                      setIsWaitingForMoreSpeech(false);
                      setAccumulatedFinalResults([]);

                      // Set timeout to go back to waiting for wake word
                      if (wakeWordTimeoutRef.current) {
                        clearTimeout(wakeWordTimeoutRef.current);
                      }
                      wakeWordTimeoutRef.current = setTimeout(() => {
                        log('⏰ Wake word session expired - waiting for wake word again');
                        setIsWaitingForWakeWord(true);
                        setWakeWordActiveUntil(null);
                      }, 10000);

                      return [];
                    } else {
                      // No wake word detected - ignore the text
                      log('🔇 Ignoring text (no wake word):', completeText);
                      setTranscript('');
                      setInterimTranscript('');
                      setIsProcessing(false);
                      setIsWaitingForMoreSpeech(false);
                      setAccumulatedFinalResults([]);
                      return [];
                    }
                  } else if (!isWaitingForWakeWordRef.current) {
                    // We're in an active wake word session - process the command normally
                    log('✅ Processing command in active wake word session');
                    // Timer was already reset in interim results, so just process normally
                  } else {
                    // We're waiting for wake word but didn't get it - ignore the text
                    log('🔇 Ignoring text (waiting for wake word):', completeText);
                    setTranscript('');
                    setInterimTranscript('');
                    setIsProcessing(false);
                    setIsWaitingForMoreSpeech(false);
                    setAccumulatedFinalResults([]);
                    return [];
                  }
                }

                // Check for stop phrases - must be exactly one of these with nothing else
                const stopPhrases = [
                  'stop voice',
                  'stop voice mode',
                  'end voice',
                  'end voice mode',
                  'cancel voice mode',
                  'exit voice',
                  'exit voice mode'
                ];

                if (stopPhrases.includes(completeText)) {
                  log('🛑 Stop phrase detected:', completeText, '- gracefully stopping voice interface');
                  // Clear everything and stop voice mode
                  setTranscript('');
                  setInterimTranscript('');
                  setIsProcessing(false);
                  setIsWaitingForMoreSpeech(false);
                  setAccumulatedFinalResults([]);
                  setWakeWord(null);
                  setIsWaitingForWakeWord(false);
                  setWakeWordActiveUntil(null);
                  if (wakeWordTimeoutRef.current) {
                    clearTimeout(wakeWordTimeoutRef.current);
                    wakeWordTimeoutRef.current = null;
                  }
                  setContinuousMode(false);
                  isManualStopRef.current = true;
                  if (recognitionRef.current) {
                    recognitionRef.current.stop();
                  }
                  return [];
                }

                // Normal processing for other commands
                setTranscript(completeText);
                setInterimTranscript('');
                setIsProcessing(true);
                setIsWaitingForMoreSpeech(false); // Clear waiting state

                // Clear accumulated results after processing
                setAccumulatedFinalResults([]);
                return [];
              });
            }, continuousSilenceTimeout);
          } else {
            // In single mode, process immediately
            setTranscript(final.trim());
            setInterimTranscript('');
            setIsProcessing(true);
          }
        } else {
          // Set actively speaking when we get interim results
          setIsActivelySpeaking(Boolean(interim.trim()));

          // Clear waiting state when new speech starts
          if (interim.trim()) {
            setIsWaitingForMoreSpeech(false);

            // Check for wake word in interim results for real-time activation
            if (wakeWordRef.current && isWaitingForWakeWordRef.current) {
              const interimText = interim.toLowerCase().trim();
              if (interimText.includes(wakeWordRef.current.toLowerCase())) {
                log('🔔 Wake word detected in interim results:', wakeWordRef.current);
                // Immediately activate the session
                const currentTime = Date.now();
                setIsWaitingForWakeWord(false);
                setWakeWordActiveUntil(currentTime + 10000);

                // Set timeout to go back to waiting for wake word
                if (wakeWordTimeoutRef.current) {
                  clearTimeout(wakeWordTimeoutRef.current);
                }
                wakeWordTimeoutRef.current = setTimeout(() => {
                  log('⏰ Wake word session expired - waiting for wake word again');
                  setIsWaitingForWakeWord(true);
                  setWakeWordActiveUntil(null);
                }, 10000);
              }
            }

            // Reset wake word timer immediately when speech is detected during active session
            if (wakeWordRef.current && !isWaitingForWakeWordRef.current) {
              // We have a wake word and we're not waiting for it, so we're in an active session
              const currentTime = Date.now();
              log('🔄 Resetting wake word timer on speech detection - wake word:', wakeWordRef.current, 'waiting:', isWaitingForWakeWordRef.current);
              setWakeWordActiveUntil(currentTime + 10000); // Reset to 10 seconds

              // Clear and reset the timeout
              if (wakeWordTimeoutRef.current) {
                clearTimeout(wakeWordTimeoutRef.current);
              }
              wakeWordTimeoutRef.current = setTimeout(() => {
                log('⏰ Wake word session expired - waiting for wake word again');
                setIsWaitingForWakeWord(true);
                setWakeWordActiveUntil(null);
              }, 10000);
            } else if (wakeWordRef.current) {
              log('⚠️ Wake word exists but not resetting timer - wake word:', wakeWordRef.current, 'waiting:', isWaitingForWakeWordRef.current);
            }
          }

          // Handle interim results
          if (continuousModeRef.current) {
            // In continuous mode, combine accumulated final results with current interim
            setAccumulatedFinalResults(prev => {
              const accumulatedText = prev.join(' ');

              // Filter transcription based on wake word state
              let displayText = '';
              if (wakeWordRef.current) {
                if (isWaitingForWakeWordRef.current) {
                  // Hide all transcription when waiting for wake word
                  displayText = '';
                } else {
                  // Show transcription without wake word when session is active
                  const fullText = accumulatedText ? `${accumulatedText} ${interim}` : interim;
                  const wakeWordLower = wakeWordRef.current.toLowerCase();

                  // Remove wake word from the beginning of the text (case insensitive)
                  displayText = fullText.toLowerCase().startsWith(wakeWordLower)
                    ? fullText.substring(wakeWordRef.current.length).trim()
                    : fullText;
                }
              } else {
                // No wake word set - show normal transcription
                displayText = accumulatedText ? `${accumulatedText} ${interim}` : interim;
              }

              setInterimTranscript(displayText);
              return prev;
            });

            // If we have a pending timeout and get new speech, reset it
            if (finalResultTimeoutRef.current) {
              clearTimeout(finalResultTimeoutRef.current);
              finalResultTimeoutRef.current = setTimeout(() => {
                setAccumulatedFinalResults(prev => {
                  const completeText = prev.join(' ');
                  // log('⏰ Processing delayed final result with interim:', `${completeText} ${interim}`);
                  setTranscript(`${completeText} ${interim}`.trim());
                  setInterimTranscript('');
                  setIsProcessing(true);
                  setIsWaitingForMoreSpeech(false);
                  setAccumulatedFinalResults([]);
                  return [];
                });
              }, continuousSilenceTimeout);
            }
          } else {
            // Single mode - apply wake word filtering to interim results
            let displayText = interim;
            if (wakeWordRef.current) {
              if (isWaitingForWakeWordRef.current) {
                // Hide all transcription when waiting for wake word
                displayText = '';
              } else {
                // Show transcription without wake word when session is active
                const wakeWordLower = wakeWordRef.current.toLowerCase();
                displayText = interim.toLowerCase().startsWith(wakeWordLower)
                  ? interim.substring(wakeWordRef.current.length).trim()
                  : interim;
              }
            }
            setInterimTranscript(displayText);
          }

          // Reset silence timeout when we get interim results
          if (interim.trim() && silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            const timeoutDuration = continuousModeRef.current ? continuousSilenceTimeout : singleSilenceTimeout;

            silenceTimeoutRef.current = setTimeout(() => {
              // In continuous mode, don't stop on silence - just clear interim
              if (continuousModeRef.current) {
                // log('🔇 Silence timeout in continuous mode - clearing interim');
                setInterimTranscript('');
                setIsActivelySpeaking(false); // Clear speaking state on timeout
              } else {
                // Only stop if not in continuous mode
                if (recognitionRef.current && isListening) {
                  // log('🔇 Silence timeout - stopping recognition');
                  recognitionRef.current.stop();
                }
              }
            }, timeoutDuration);
          }
        }
      };

      // Handle recognition start
      recognition.onstart = () => {
        setIsListening(true);
        setError(null);
        setTranscript('');
        setInterimTranscript('');
        setConfidence(0);
        // Clear accumulated results when starting fresh
        setAccumulatedFinalResults([]);

        // In continuous mode, don't set timeouts that would stop recognition
        if (!continuousModeRef.current) {
          // Set overall timeout only for non-continuous mode
          timeoutRef.current = setTimeout(() => {
            if (recognitionRef.current) {
              recognitionRef.current.stop();
            }
          }, RECOGNITION_TIMEOUT);

          // Set silence timeout only for non-continuous mode
          silenceTimeoutRef.current = setTimeout(() => {
            if (recognitionRef.current) {
              recognitionRef.current.stop();
            }
          }, singleSilenceTimeout);
        } else {
          // In continuous mode, set a longer initial silence timeout for the first words
          const timeoutDuration = continuousSilenceTimeout;
          silenceTimeoutRef.current = setTimeout(() => {
            // log('🔇 Initial silence timeout in continuous mode');
            setInterimTranscript('');
          }, timeoutDuration);
        }
      };

      // Handle recognition end
      recognition.onend = () => {
        // log('🛑 Recognition ended, continuous mode:', continuousModeRef.current, 'manual stop:', isManualStopRef.current);

        // Always set listening to false when recognition ends
        setIsListening(false);
        clearTimeouts();

        // In continuous mode, automatically restart recognition
        if (continuousModeRef.current && !isManualStopRef.current) {
          // log('🔄 Scheduling restart in continuous mode');
          forceRestart();
        } else {
          // log('🛑 Not restarting - continuous:', continuousModeRef.current, 'manual:', isManualStopRef.current);
          // If we don't have a final transcript, it was cancelled or failed
          if (!transcript && !interimTranscript) {
            setIsProcessing(false);
          }
        }
      };

      // Handle recognition errors
      recognition.onerror = (event) => {
        // log('🚨 Recognition error:', event.error, 'continuous mode:', continuousModeRef.current);

        switch (event.error) {
          case 'no-speech':
            // In continuous mode, "no-speech" is expected during restarts - don't treat as error
            if (continuousModeRef.current) {
              // log('⚠️ No speech detected in continuous mode - ignoring error');
              // Don't set error or stop listening - let the normal restart flow handle it
              return;
            } else {
              setIsListening(false);
              setIsProcessing(false);
              clearTimeouts();
              setError('No speech detected. Please try again.');
            }
            break;
          case 'aborted':
            // Aborted is normal when we manually stop
            if (isManualStopRef.current) {
              // log('🛑 Recognition aborted manually - not an error');
              setIsListening(false);
              setIsProcessing(false);
              clearTimeouts();
              setError(null); // Clear any existing errors
              return;
            }
            // Fall through to default handling if not manual
          case 'audio-capture':
          case 'not-allowed':
          case 'network':
          default:
            // These are serious errors that should stop continuous mode
            setIsListening(false);
            setIsProcessing(false);
            clearTimeouts();
            setContinuousMode(false); // Disable continuous mode on serious errors
            isManualStopRef.current = true; // Prevent restart attempts

            let errorMessage = 'Voice recognition error';
            switch (event.error) {
              case 'audio-capture':
                errorMessage = 'Microphone not available. Please check permissions.';
                break;
              case 'not-allowed':
                errorMessage = 'Microphone access denied. Please enable microphone permissions.';
                break;
              case 'network':
                errorMessage = 'Network error. Please check your connection.';
                break;
              case 'aborted':
                errorMessage = 'Voice recognition was stopped.';
                break;
              default:
                errorMessage = `Voice recognition error: ${event.error}`;
            }
            setError(errorMessage);
        }
      };

      recognitionRef.current = recognition;
    } else {
      setIsSupported(false);
      setError('Voice recognition is not supported in this browser. Please use Chrome, Edge, or Safari.');
    }

    return () => {
      clearTimeouts();
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const clearTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
    if (finalResultTimeoutRef.current) {
      clearTimeout(finalResultTimeoutRef.current);
      finalResultTimeoutRef.current = null;
    }
  }, []);

  // Force restart recognition in continuous mode
  const forceRestart = useCallback(() => {
    if (!continuousModeRef.current || isManualStopRef.current) return;

    // log('🔄 Force restarting recognition...');
    clearTimeouts();

    restartTimeoutRef.current = setTimeout(() => {
      if (continuousModeRef.current && !isManualStopRef.current && recognitionRef.current) {
        try {
          // Check if recognition is already running
          if (isListening) {
            // log('🔄 Recognition already active, skipping restart');
            return;
          }

          // log('🎤 Starting fresh recognition instance');
          recognitionRef.current.start();
        } catch (error) {
          console.warn('Recognition restart failed:', error.message);
          // If we get "already started" error, that's actually good
          if (!error.message.includes('already started')) {
            console.error('Continuous mode error, stopping:', error);
            setContinuousMode(false);
            setIsListening(false);
            setError('Voice recognition stopped due to error');
          }
        }
      }
    }, 500); // Longer delay for more reliable restart
  }, [isListening, clearTimeouts]);

  // Continuous mode monitoring - ensure recognition stays active
  useEffect(() => {
    if (!continuousMode) return;

    const monitorInterval = setInterval(() => {
      // If we're in continuous mode but not listening and not processing, restart
      if (continuousModeRef.current && !isListening && !isProcessing && !isManualStopRef.current) {
        // log('🔍 Monitoring detected inactive recognition, restarting...');
        forceRestart();
      }
    }, 2000); // Check every 2 seconds

    return () => clearInterval(monitorInterval);
  }, [continuousMode, isListening, isProcessing, forceRestart]);

  const startListening = useCallback(() => {
    if (!isSupported || !recognitionRef.current || isListening || isProcessing) {
      return;
    }

    try {
      setError(null);
      setContinuousMode(true); // Enable continuous mode
      isManualStopRef.current = false; // Reset manual stop flag
      log('🎤 Starting continuous voice mode');
      recognitionRef.current.start();
    } catch (error) {
      console.error('Failed to start voice recognition:', error);
      setError('Failed to start voice recognition. Please try again.');
    }
  }, [isSupported, isListening, isProcessing]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      log('🛑 Manually stopping voice recognition');
      setContinuousMode(false); // Disable continuous mode
      isManualStopRef.current = true; // Set manual stop flag
      recognitionRef.current.stop();
    }
  }, [isListening]);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setConfidence(0);
    setIsProcessing(false);
    setError(null);
  }, []);

  const resetTranscriptOnly = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setConfidence(0);
    setIsProcessing(false);
    // Keep listening active, don't clear error or stop recognition
  }, []);

  const cancelListening = useCallback(() => {
    // log('❌ Canceling voice recognition');
    setContinuousMode(false); // Disable continuous mode
    isManualStopRef.current = true; // Set manual stop flag
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }
    setIsListening(false);
    setIsProcessing(false);
    setTranscript('');
    setInterimTranscript('');
    setError(null);
    setAccumulatedFinalResults([]); // Clear accumulated results
    setIsWaitingForMoreSpeech(false); // Clear waiting state
    clearTimeouts();
  }, [clearTimeouts]);

  return {
    // State
    isSupported,
    isListening,
    isProcessing,
    transcript,
    interimTranscript,
    error,
    confidence,
    continuousMode,
    isActivelySpeaking,
    isWaitingForMoreSpeech,

    // Wake word states
    wakeWord,
    isWaitingForWakeWord,
    wakeWordActiveUntil,

    // Combined transcript for display
    displayTranscript: transcript || interimTranscript,

    // Actions
    startListening,
    stopListening,
    cancelListening,
    resetTranscript,
    resetTranscriptOnly,

    // Status helpers
    isActive: isListening || isProcessing,
    hasResult: Boolean(transcript),
    hasError: Boolean(error)
  };
}